/**
 * Contact Form Webhook Integration
 *
 * Modified: 2026-02-13
 * Author: Claude (AI Assistant)
 * Purpose: Intercept contact form submissions and send data to an external webhook URL
 * Scope: Supports three modes - webhook only, both (webhook + Shopify), and Shopify only (no-op).
 *        Handles CORS fallback with no-cors mode, displays success/error messages matching
 *        the theme's native alert styles.
 */

(function () {
  'use strict';

  var SKIP_FIELDS = ['authenticity_token', 'utf8', 'form_type'];

  function init() {
    var configScripts = document.querySelectorAll('script[id^="ContactFormWebhookConfig-"]');
    configScripts.forEach(function (script) {
      try {
        var config = JSON.parse(script.textContent);
        if (!config.webhookUrl) return;
        var form = document.getElementById(config.formId);
        if (!form) return;
        attachListener(form, config);
      } catch (e) {
        console.error('[ContactFormWebhook] Failed to parse config:', e);
      }
    });
  }

  function attachListener(form, config) {
    var submittingToShopify = false;

    function handleSubmit(e) {
      // If we're in the native Shopify submission pass-through, let it go
      if (submittingToShopify) return;

      e.preventDefault();
      var submitBtn = form.querySelector('button[type="submit"]');
      setLoading(submitBtn, true);
      clearMessages(form);

      var payload = buildPayload(form);
      sendWebhook(config.webhookUrl, payload)
        .then(function () {
          if (config.mode === 'webhook') {
            showSuccess(form, config.successMessage || 'Thank you for contacting us. We\'ll get back to you shortly.');
            form.reset();
            setLoading(submitBtn, false);
          } else if (config.mode === 'both') {
            // Submit natively to Shopify by setting flag and re-submitting
            submittingToShopify = true;
            form.submit();
          }
        })
        .catch(function (err) {
          console.error('[ContactFormWebhook] Error:', err);
          showError(form, 'Something went wrong. Please try again later.');
          setLoading(submitBtn, false);
        });
    }

    form.addEventListener('submit', handleSubmit);
  }

  function buildPayload(form) {
    var formData = new FormData(form);
    var payload = {};

    formData.forEach(function (value, key) {
      // Skip Shopify internal fields
      for (var i = 0; i < SKIP_FIELDS.length; i++) {
        if (key === SKIP_FIELDS[i]) return;
      }

      // Strip "contact[...]" wrapper
      var cleanKey = key;
      var match = key.match(/^contact\[(.+)\]$/);
      if (match) {
        cleanKey = match[1];
      }

      // Convert to snake_case
      var snakeKey = cleanKey
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');

      // Handle multiple values for same key (e.g., checkboxes)
      if (payload[snakeKey] !== undefined) {
        payload[snakeKey] = value;
      } else {
        payload[snakeKey] = value;
      }
    });

    payload.submitted_at = new Date().toISOString();
    payload.page_url = window.location.href;

    return payload;
  }

  function sendWebhook(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors'
    }).then(function (response) {
      if (response.ok) return response;
      throw new Error('Webhook responded with status ' + response.status);
    }).catch(function (err) {
      // If CORS error, retry with no-cors (opaque response but request still sent)
      if (err instanceof TypeError) {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
          mode: 'no-cors'
        });
      }
      throw err;
    });
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.classList.add('btn--loading');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.classList.remove('btn--loading');
      btn.removeAttribute('aria-disabled');
    }
  }

  function clearMessages(form) {
    var existing = form.querySelectorAll('.form-status-list, .form-error, .webhook-status-message');
    existing.forEach(function (el) { el.remove(); });
  }

  function showSuccess(form, message) {
    var wrapper = document.createElement('div');
    wrapper.className = 'form-status-list webhook-status-message';
    wrapper.setAttribute('role', 'status');
    wrapper.innerHTML =
      '<h3 class="form-message alert alert--success h6 blocks-radius" tabindex="-1" autofocus>' +
        '<svg aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 12.35C9.7 12.35 12.35 9.7 12.35 6.5C12.35 3.3 9.7 0.65 6.5 0.65C3.3 0.65 0.65 3.3 0.65 6.5C0.65 9.7 3.3 12.35 6.5 12.35Z" fill="currentColor" stroke="currentColor" stroke-width="0.7"/><path d="M5.53271 8.66357L9.25372 4.68196" stroke="white" stroke-width="0.7"/><path d="M4.10645 6.7688L5.27163 8.30639" stroke="white" stroke-width="0.7"/></svg> ' +
        escapeHtml(message) +
      '</h3>';
    form.insertBefore(wrapper, form.firstChild);
    wrapper.querySelector('h3').focus();
  }

  function showError(form, message) {
    var wrapper = document.createElement('div');
    wrapper.className = 'form-error webhook-status-message';
    wrapper.innerHTML =
      '<h3 class="form-message form-message--error h6 m-0 alert alert--error blocks-radius" role="alert" tabindex="-1" autofocus>' +
        '<svg aria-hidden="true" focusable="false" width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="6.5" cy="6.5" r="5.85" fill="currentColor" stroke="currentColor" stroke-width="0.7"/><path d="M5.87413 3.52832L5.97439 7.57216H7.01345L7.11371 3.52832H5.87413ZM6.49392 9.40424C6.88572 9.40424 7.14932 9.15247 7.14932 8.79645C7.14932 8.44044 6.88572 8.18867 6.49392 8.18867C6.10212 8.18867 5.83852 8.44044 5.83852 8.79645C5.83852 9.15247 6.10212 9.40424 6.49392 9.40424Z" fill="white"/></svg> ' +
        escapeHtml(message) +
      '</h3>';
    form.insertBefore(wrapper, form.firstChild);
    wrapper.querySelector('h3').focus();
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
